import { io, Socket } from 'socket.io-client';
import { WSConnectionStatus, WSEntityCreatedMsg, WSEntityIdChangedMsg, WSEntitySkippedAsNoLicenseMsg, WSMessageType } from '../types/websocket';

const WS_URL = process.env.REACT_APP_WS_URL || '';

type WSMessageHandler = {
  type: WSMessageType;
  callback: (message: any) => void;
};

export class WSService {
  private socket: Socket | null = null;
  private connectionStatus: WSConnectionStatus = 'disconnected';
  private statusListeners: Array<(status: WSConnectionStatus) => void> = [];
  private messageListeners: Array<WSMessageHandler> = [];

  constructor() {
    this.connect();
  }

  private connect(): void {
    this.setConnectionStatus('connecting');

    // Set a timeout to switch to disconnected if connection takes too long
    const connectionTimeout = setTimeout(() => {
      if (this.connectionStatus === 'connecting') {
        console.warn('WebSocket connection timeout, switching to disconnected');
        this.setConnectionStatus('disconnected');
      }
    }, 15000);

    // Get auth token from localStorage
    const authToken = localStorage.getItem('authToken');

    this.socket = io(WS_URL, {
      transports: ['websocket'],
      timeout: 10000,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 5000,
      auth: {
        token: authToken || undefined,
      },
      query: authToken ? {
        token: authToken,
      } : undefined,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected to:', WS_URL);
      clearTimeout(connectionTimeout);
      this.setConnectionStatus('connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.setConnectionStatus('disconnected');
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      clearTimeout(connectionTimeout);
      this.setConnectionStatus('error');
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed');
      this.setConnectionStatus('error');
    });

    // Listen for entity ID changed messages
    this.socket.on(WSMessageType.entityIdChanged, (message: WSEntityIdChangedMsg) => {
      console.log('Received entity ID changed message:', message);
      this.notifyMessageListeners(WSMessageType.entityIdChanged, message);
    });

    // Listen for entity created messages
    this.socket.on(WSMessageType.entityCreated, (message: WSEntityCreatedMsg) => {
      console.log('Received entity created message:', message);
      this.notifyMessageListeners(WSMessageType.entityCreated, message);
    });

    // Listen for entity skipped as no license messages
    this.socket.on(WSMessageType.entitySkippedAsNoLicense, (message: WSEntitySkippedAsNoLicenseMsg) => {
      console.log('Received entity skipped as no license message:', message);
      this.notifyMessageListeners(WSMessageType.entitySkippedAsNoLicense, message);
    });
  }

  private setConnectionStatus(status: WSConnectionStatus): void {
    this.connectionStatus = status;
    this.notifyStatusListeners(status);
  }

  private notifyStatusListeners(status: WSConnectionStatus): void {
    this.statusListeners.forEach(listener => listener(status));
  }

  private notifyMessageListeners(type: WSMessageType, message: any): void {
    this.messageListeners
      .filter(handler => handler.type === type)
      .forEach(handler => handler.callback(message));
  }

  public getConnectionStatus(): WSConnectionStatus {
    return this.connectionStatus;
  }

  public onStatusChange(callback: (status: WSConnectionStatus) => void): () => void {
    this.statusListeners.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.statusListeners.indexOf(callback);
      if (index > -1) {
        this.statusListeners.splice(index, 1);
      }
    };
  }

  public onMessage<T = any>(type: WSMessageType, callback: (message: T) => void): () => void {
    const handler: WSMessageHandler = { type, callback };
    this.messageListeners.push(handler);

    // Return unsubscribe function
    return () => {
      const index = this.messageListeners.indexOf(handler);
      if (index > -1) {
        this.messageListeners.splice(index, 1);
      }
    };
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.setConnectionStatus('disconnected');
    }
  }

  public reconnect(): void {
    this.disconnect();
    // Small delay to ensure disconnect is complete
    setTimeout(() => {
      this.connect();
    }, 100);
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// Create singleton instance
export const wsService = new WSService();
